/**
 * Por clasificar — los camiones.
 *
 * 🚨 Rehecha sobre la propuesta que Luis aprobó completa, el 21-ago-2026.
 *
 * 📊 El hallazgo que la ordena: de **566 camiones sin clasificar, 31 concentran
 * el 58 % de los viajes** y **164 tienen un camión en la calle hoy**. La
 * pantalla vieja los mostraba a todos por igual, en el orden en que llegaron,
 * y cortaba en 50 sin decir cuántos quedaban afuera.
 *
 * Las seis cosas que cambian, todas pedidas:
 *   1. Orden por impacto, no por lista
 *   2. Cada fila dice cuánto vale la decisión
 *   3. Marcar varios a la vez — con 566, uno por uno no lo hace nadie
 *   4. Sugerencia con el dato que ya existe
 *   5. Filtros de verdad
 *   6. Barra de avance
 */

import { useCallback, useEffect, useState } from 'react'
import { pedirConSesion, puedeCambiar } from './Entrar.tsx'
import { usarPaginas, PieDeTabla } from './Tabla.tsx'
import { cedula as conPuntos, comoFecha } from './formato.ts'

interface Camion {
  id: string
  placa: string
  tipo_vehiculo: string | null
  creado_at: string | null
  viajes: number | string
  viajes_mes: number | string
  en_calle: number | string
}

interface Conductor {
  id: string
  cedula: string
  nombre: string | null
  estado: string
  /** Para poder mandarle la clave. Hoy casi ninguno lo tiene. */
  celular: string | null
  viajes: number | string
  viajes_mes: number | string
  en_calle: number | string
  placas: string | null
  ultimo_viaje: string | null
}

interface Resumen {
  faltan: number
  yaClasificados: number
  mucho: number
  medio: number
  poco: number
  conViaje: number
  viajesTotales: number
  viajesDeLosGrandes: number
}

/** «hace 8 meses». Cuánto lleva esperando una decisión. */
/*
 * 🚨 Las fechas se parsean con `comoFecha`, NUNCA con `new Date()` a pelo.
 *
 * Postgres devuelve `2026-08-20 19:23:50.737-05` y JavaScript **no sabe leer
 * ese `-05`**: el estándar pide `-05:00`. La fecha queda inválida y la pantalla
 * dice «Nunca» sobre alguien que sí entró — se vio así, con una fila diciendo
 * «Nunca entró» y al lado «Entró, pero no ha entregado».
 *
 * ⛔ El error estaba en CINCO pantallas porque cada vez volví a escribir la
 * función en vez de usar la que ya existía en `formato.ts`.
 */
function haceCuanto(iso: string | null): string {
  if (!iso) return '—'
  const f = comoFecha(iso)
  if (!f) return '—'
  const dias = Math.floor((Date.now() - f.getTime()) / 86400000)
  if (dias < 1) return 'hoy'
  if (dias < 7) return `hace ${dias} ${dias === 1 ? 'día' : 'días'}`
  if (dias < 31) return `hace ${Math.floor(dias / 7)} semanas`
  const meses = Math.floor(dias / 30)
  return meses < 12 ? `hace ${meses} meses` : `hace ${Math.floor(meses / 12)} años`
}

function fechaCorta(iso: string | null): string {
  if (!iso) return ''
  const f = comoFecha(iso)
  return f ? f.toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' }) : ''
}

type Cual = 'camiones' | 'conductores'

/**
 * El mensaje que se le manda al conductor, listo para pegar.
 *
 * ⭐ Lleva todo lo que necesita para entrar sin que nadie le explique: con qué
 * entra, la clave, y el enlace. Y le dice que la cambie, porque la clave se
 * dictó o se escribió y ya no es secreta.
 */
function mensajeParaElConductor(cedula: string, clave: string): string {
  return [
    'Hola, le habla Grupo S&M.',
    '',
    'Ya puede ver sus viajes y registrar sus entregas desde el celular:',
    'https://symtrack.dgnova.pro/app/',
    '',
    `Entra con su cédula ${cedula} y esta clave: ${clave}`,
    '',
    'Apenas entre, cámbiela por una que solo usted sepa.',
  ].join(String.fromCharCode(10))
}

export function Clasificar({
  alIrA,
  totales,
}: {
  alIrA?: ((p: string) => void) | undefined
  /** Vienen del menú, para que la pestaña diga cuánto falta sin tener que entrar. */
  totales?: { camiones: number; conductores: number; clientes: number } | undefined
}) {
  const [cual, setCual] = useState<Cual>('camiones')
  const [camiones, setCamiones] = useState<Camion[] | null>(null)
  const [conductores, setConductores] = useState<Conductor[] | null>(null)
  const [resumenConductores, setResumenConductores] = useState<Resumen | null>(null)
  /** Las claves recién generadas. Se muestran UNA vez. */
  const [claves, setClaves] = useState<
    { cedula: string; nombre: string | null; clave: string; celular: string | null }[] | null
  >(null)
  /*
   * 🚨 El celular de quien va a recibir la clave.
   *
   * 📊 Medido: de 589 conductores, CERO tienen celular guardado. Sin él, se
   * generan las claves y no hay cómo hacérselas llegar. Se pide aquí, opcional,
   * porque muchas veces la oficina le da la clave al conductor cuando pasa por
   * la planta y no tiene el número a mano.
   */
  const [celular, setCelular] = useState('')
  const [pidiendoCelular, setPidiendoCelular] = useState<Conductor | null>(null)
  const [resumen, setResumen] = useState<Resumen | null>(null)
  const [orden, setOrden] = useState('impacto')
  const [mueve, setMueve] = useState('')
  const [conViaje, setConViaje] = useState(false)
  const [desde, setDesde] = useState('')
  const [busca, setBusca] = useState('')
  const [marcados, setMarcados] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const [guardando, setGuardando] = useState(false)

  const cargar = useCallback(async () => {
    const comun =
      `orden=${orden}&mueve=${mueve}&conViaje=${conViaje ? 'si' : ''}` +
      `&busca=${encodeURIComponent(busca)}`
    try {
      if (cual === 'camiones') {
        const r = await pedirConSesion<{ camiones: Camion[]; resumen: Resumen }>(
          `/api/admin/clasificar/camiones?${comun}&desde=${desde}`,
        )
        setCamiones(r.camiones)
        setResumen(r.resumen)
      } else {
        const r = await pedirConSesion<{ conductores: Conductor[]; resumen: Resumen }>(
          `/api/admin/clasificar/conductores?${comun}`,
        )
        setConductores(r.conductores)
        setResumenConductores(r.resumen)
      }
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar')
    }
  }, [cual, orden, mueve, conViaje, desde, busca])

  useEffect(() => {
    void cargar()
  }, [cual, orden, mueve, conViaje, desde, cargar])

  const n = (v: number | string) => Number(v ?? 0)
  const esCamiones = cual === 'camiones'
  const resumenActivo = esCamiones ? resumen : resumenConductores
  const listaActiva: (Camion | Conductor)[] = esCamiones ? (camiones ?? []) : (conductores ?? [])
  const pag = usarPaginas(listaActiva)

  async function clasificar(ids: string[], propiedad: 'PROPIO' | 'TERCERO') {
    if (guardando || ids.length === 0) return
    setGuardando(true)
    setError(null)
    try {
      const r = await pedirConSesion<{ mensaje: string }>('/api/admin/clasificar/camiones', {
        method: 'POST',
        body: JSON.stringify({ ids, propiedad }),
      })
      setAviso(r.mensaje)
      setMarcados(new Set())
      await cargar()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar')
    } finally {
      setGuardando(false)
    }
  }

  async function darAcceso(ids: string[], numero = '') {
    if (guardando || ids.length === 0) return
    setGuardando(true)
    setError(null)
    try {
      const r = await pedirConSesion<{
        claves: { cedula: string; nombre: string | null; clave: string; celular: string | null }[]
        bloqueados: string[]
        mensaje: string
      }>('/api/admin/clasificar/conductores/acceso', {
        method: 'POST',
        body: JSON.stringify({ ids, celular: numero }),
      })
      setClaves(r.claves)
      setAviso(
        r.bloqueados.length > 0
          ? `${r.mensaje} Quedaron por fuera ${r.bloqueados.length} que están bloqueados.`
          : r.mensaje,
      )
      setMarcados(new Set())
      setPidiendoCelular(null)
      setCelular('')
      await cargar()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo dar el acceso')
    } finally {
      setGuardando(false)
    }
  }

  function alternar(id: string) {
    setMarcados((antes) => {
      const s = new Set(antes)
      if (s.has(id)) s.delete(id)
      else s.add(id)
      return s
    })
  }

  const enPantallaIds = pag.enPantalla.map((c) => c.id)
  const todosMarcados = enPantallaIds.length > 0 && enPantallaIds.every((i) => marcados.has(i))
  const total = (resumenActivo?.faltan ?? 0) + (resumenActivo?.yaClasificados ?? 0)
  const avance = total > 0 ? Math.round(((resumenActivo?.yaClasificados ?? 0) / total) * 100) : 0
  const porcentajeGrandes =
    resumenActivo && resumenActivo.viajesTotales > 0
      ? Math.round((resumenActivo.viajesDeLosGrandes / resumenActivo.viajesTotales) * 100)
      : 0
  /** «camiones» o «conductores», para no repetir el condicional en cada texto. */
  const queSon = esCamiones ? 'camiones' : 'conductores'
  const corte = esCamiones ? 50 : 20

  return (
    <section>
      <div className="tit-fila">
        <h1>Por clasificar</h1>
        <span>Decisiones que solo puede tomar una persona, y que están frenando la operación</span>
      </div>

      {/*
       * ⭐ EL HALLAZGO, arriba de todo. Es lo que convierte una lista de 566 en
       * un plan de trabajo: no todos valen lo mismo.
       */}
      {resumenActivo && resumenActivo.mucho > 0 && (
        <div className="hallazgo">
          <div className="hallazgo-n num">
            {total > 0 ? Math.max(1, Math.round((resumenActivo.mucho / resumenActivo.faltan) * 100)) : 0}%
          </div>
          <div>
            <h2>No todos valen lo mismo</h2>
            <p>
              De los <b className="num">{resumenActivo.faltan}</b> {queSon} sin clasificar, solo{' '}
              <b className="num">{resumenActivo.mucho}</b> mueven de verdad — y esos concentran{' '}
              <b className="num">{resumenActivo.viajesDeLosGrandes.toLocaleString('es-CO')}</b> de{' '}
              <b className="num">{resumenActivo.viajesTotales.toLocaleString('es-CO')}</b> viajes:{' '}
              <b className="num">el {porcentajeGrandes}%</b>. Empieza por esos. Los{' '}
              <b className="num">{resumenActivo.poco}</b> que tienen un viaje o ninguno pueden esperar.
            </p>
          </div>
        </div>
      )}

      {resumenActivo && (
        <div className="kpis kpis-4">
          <article className="kpi">
            <p className="kpi-et">{esCamiones ? 'Con camión en la calle hoy' : 'Rodando hoy sin la app'}</p>
            <p className="kpi-n num" style={resumenActivo.conViaje > 0 ? { color: '#CA2B2B' } : undefined}>
              {resumenActivo.conViaje}
            </p>
            <p className="kpi-pie">{esCamiones ? 'Rodando sin saber de quién son' : 'Su entrega no queda registrada'}</p>
          </article>
          <article className="kpi">
            <p className="kpi-et">Mueven mucho</p>
            <p className="kpi-n num">{resumenActivo.mucho}</p>
            <p className="kpi-pie">{corte} viajes o más · el {porcentajeGrandes}% del total</p>
          </article>
          <article className="kpi">
            <p className="kpi-et">Movimiento medio</p>
            <p className="kpi-n num">{resumenActivo.medio}</p>
            <p className="kpi-pie">Entre 2 y {corte - 1} viajes</p>
          </article>
          <article className="kpi">
            <p className="kpi-et">Casi no mueven</p>
            <p className="kpi-n num">{resumenActivo.poco}</p>
            <p className="kpi-pie">Un viaje o ninguno · pueden esperar</p>
          </article>
        </div>
      )}

      {/* ⛔ EL ORDEN NO SE TOCA: título → indicadores → una caja → tabla. */}
      <div className="filtros-barra">
        {/* Una lista a la vez, y la pestaña dice cuánto falta en la otra. */}
        <div className="pestanas">
          <button
            onClick={() => {
              setCual('camiones')
              setMarcados(new Set())
              setMueve('')
              setBusca('')
            }}
            className={`pestana ${esCamiones ? 'pestana-on' : ''}`}
          >
            Camiones
            <b className="num">{resumen?.faltan ?? totales?.camiones ?? 0}</b>
          </button>
          <button
            onClick={() => {
              setCual('conductores')
              setMarcados(new Set())
              setMueve('')
              setBusca('')
              setDesde('')
            }}
            className={`pestana ${!esCamiones ? 'pestana-on' : ''}`}
          >
            Conductores
            <b className="num">{resumenConductores?.faltan ?? totales?.conductores ?? 0}</b>
          </button>
          {/*
           * ⛔ Los clientes sin portal NO van aquí: ya tienen su propia
           * pantalla, con búsqueda y activación. Duplicarla sería dos sitios
           * haciendo lo mismo, que mañana se contradicen — el error que ya
           * costó caro cuando el control de acceso vivía en dos guardias.
           */}
          <button className="pestana pestana-ir" onClick={() => alIrA?.('clientes')}>
            Clientes sin portal
            {totales?.clientes ? <b className="num">{totales.clientes}</b> : null}
            <span className="pestana-flecha">→</span>
          </button>
        </div>

        <div className="filtros-fila">
          <div className="campo">
            <label htmlFor="c-orden">Ordenar por</label>
            <select id="c-orden" value={orden} onChange={(e) => setOrden(e.target.value)}>
              <option value="impacto">Los que más importan primero</option>
              <option value="viaje_hoy">Los que tienen viaje hoy</option>
              <option value="recientes">Los detectados más recientes</option>
              <option value="placa">Placa (A–Z)</option>
            </select>
          </div>
          <div className="campo">
            <label htmlFor="c-mueve">Cuánto mueve</label>
            <select
              id="c-mueve"
              value={mueve}
              onChange={(e) => {
                setMueve(e.target.value)
                setConViaje(false)
              }}
            >
              <option value="">Cualquiera</option>
              <option value="mucho">Mueve mucho — {corte} o más ({resumenActivo?.mucho ?? 0})</option>
              <option value="medio">Movimiento medio — 2 a {corte - 1} ({resumenActivo?.medio ?? 0})</option>
              <option value="poco">Casi no mueve — 1 o ninguno ({resumenActivo?.poco ?? 0})</option>
            </select>
          </div>
          <div className="campo">
            <label htmlFor="c-viaje">Estado del viaje</label>
            <select
              id="c-viaje"
              value={conViaje ? 'si' : ''}
              onChange={(e) => {
                setConViaje(e.target.value === 'si')
                if (e.target.value === 'si') setMueve('')
              }}
            >
              <option value="">Cualquiera</option>
              <option value="si">{esCamiones ? 'Tiene camión en la calle hoy' : 'Está rodando hoy'} ({resumenActivo?.conViaje ?? 0})</option>
            </select>
          </div>
          {esCamiones && (
            <div className="campo">
              <label htmlFor="c-desde">Detectado desde</label>
              <input id="c-desde" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
            </div>
          )}
          <div className="campo campo-ancho">
            <label htmlFor="c-placa">{esCamiones ? 'Placa' : 'Cédula'}</label>
            <input
              id="c-placa"
              value={busca}
              onChange={(e) => setBusca(esCamiones ? e.target.value.toUpperCase() : e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void cargar()
              }}
              placeholder={esCamiones ? 'UPO640' : '1124406339'}
            />
          </div>
          <button type="button" className="btn-buscar" onClick={() => void cargar()}>
            Buscar
          </button>
          {(mueve || conViaje || desde || busca) && (
            <button
              type="button"
              className="btn-limpiar"
              onClick={() => {
                setMueve('')
                setConViaje(false)
                setDesde('')
                setBusca('')
              }}
            >
              Limpiar
            </button>
          )}
        </div>

        {/*
         * 🚨 El celular, antes de generar la clave. Es el cuello de botella del
         * piloto: sin él se generan las claves y no hay cómo entregarlas.
         * Va opcional porque muchas veces se la dictan en la planta.
         */}
        {pidiendoCelular && (
          <div className="pedir-celular">
            <div>
              <b>Darle acceso a {conPuntos(pidiendoCelular.cedula)}</b>
              <span>
                Si tienes su celular, se lo mandas por WhatsApp de una. Si no, generas la clave
                igual y se la dictas.
              </span>
            </div>
            <input
              value={celular}
              onChange={(e) => setCelular(e.target.value.replace(/\D/g, '').slice(0, 10))}
              placeholder="300 123 4567"
              inputMode="numeric"
              autoFocus
            />
            <button
              className="btn-buscar"
              disabled={guardando}
              onClick={() => void darAcceso([pidiendoCelular.id], celular)}
            >
              {guardando ? 'Generando…' : 'Generar la clave'}
            </button>
            <button
              className="btn-limpiar"
              onClick={() => {
                setPidiendoCelular(null)
                setCelular('')
              }}
            >
              Cancelar
            </button>
          </div>
        )}

        {(error || aviso) && (
          <p className={`linea-msg ${error ? 'linea-msg-mal' : 'linea-msg-bien'}`}>
            {error ?? aviso}
          </p>
        )}

        {/* Con cientos pendientes hay que ver que la pila baja, o nadie la trabaja. */}
        {resumenActivo && (
          <div className="avance-clasificar">
            <div className="riel">
              <span style={{ width: `${avance}%` }} />
            </div>
            <p>
              <b className="num">{resumenActivo.yaClasificados.toLocaleString('es-CO')}</b> de{' '}
              <b className="num">{total.toLocaleString('es-CO')}</b> {esCamiones ? 'camiones clasificados' : 'conductores con acceso'}
            </p>
          </div>
        )}
      </div>

      {/*
       * 🚨 Marcar VARIOS a la vez. Sin esto, 566 camiones uno por uno no los
       * clasifica nadie y la pila se queda ahí para siempre.
       */}
      {marcados.size > 0 && puedeCambiar() && (
        <div className="masivo">
          <b className="num">
            {marcados.size}{' '}
            {esCamiones
              ? marcados.size === 1
                ? 'camión seleccionado'
                : 'camiones seleccionados'
              : marcados.size === 1
                ? 'conductor seleccionado'
                : 'conductores seleccionados'}
          </b>

          {esCamiones ? (
            <>
              <button
                className="bmas"
                disabled={guardando}
                onClick={() => void clasificar([...marcados], 'PROPIO')}
              >
                {guardando ? 'Guardando…' : `Marcar ${marcados.size === 1 ? 'como propio' : 'todos como propios'}`}
              </button>
              <button
                className="bmas"
                disabled={guardando}
                onClick={() => void clasificar([...marcados], 'TERCERO')}
              >
                {guardando ? 'Guardando…' : `Marcar ${marcados.size === 1 ? 'como de tercero' : 'todos como de tercero'}`}
              </button>
            </>
          ) : (
            <>
              <button className="bmas" disabled={guardando} onClick={() => void darAcceso([...marcados])}>
                {guardando
                  ? 'Generando…'
                  : `Darles acceso a la app ${marcados.size === 1 ? '' : `(${marcados.size})`}`}
              </button>
              <span style={{ fontSize: 13, opacity: 0.8 }}>
                Sale una clave por cada uno, para dictarlas.
              </span>
            </>
          )}

          <button className="bmas" onClick={() => setMarcados(new Set())} style={{ marginLeft: 'auto' }}>
            Quitar la selección
          </button>
        </div>
      )}

      {/*
       * 🚨 Las claves recién generadas. Se muestran UNA sola vez: se guardan
       * cifradas y no se pueden volver a leer. Si una se pierde, se genera otra
       * y la anterior deja de servir.
       */}
      {claves && claves.length > 0 && (
        <div className="claves-nuevas">
          <div className="claves-cab">
            <b>
              {claves.length === 1
                ? 'Esta es la clave. Anótala antes de cerrar.'
                : `Estas son las ${claves.length} claves. Anótalas antes de cerrar.`}
            </b>
            <button
              className="btn-tabla"
              onClick={() => {
                void navigator.clipboard?.writeText(
                  claves.map((c) => `${c.cedula}: ${c.clave}`).join(String.fromCharCode(10)),
                )
                setAviso('Copiadas. Pégalas donde las necesites.')
              }}
            >
              Copiarlas todas
            </button>
          </div>
          <div className="claves-lista">
            {claves.map((c) => (
              <div key={c.cedula} className="clave-item">
                {/*
                  🚨 El NOMBRE primero. Esta lista se DICTA por telefono: con
                  solo la cedula y cuatro digitos, quien llama no sabe a quien
                  esta marcando.
                */}
                <span className="clave-quien">
                  {/* ⭐ Ya llega de Vulcano. La cédula va debajo de todas formas. */}
                  {c.nombre ?? conPuntos(c.cedula)}
                  <small className="num">{conPuntos(c.cedula)}</small>
                </span>
                <b className="num">{c.clave}</b>
                {/*
                 * ⛔ Es un enlace normal de WhatsApp: abre el WhatsApp de quien
                 * está en la consola. NO usa la API de pago de WhatsApp
                 * Business, que necesita autorización aparte.
                 */}
                {c.celular && (
                  <a
                    className="btn-tabla btn-wa"
                    href={`https://wa.me/57${c.celular}?text=${encodeURIComponent(
                      mensajeParaElConductor(c.cedula, c.clave),
                    )}`}
                    target="_blank"
                    rel="noopener"
                  >
                    Mandar
                  </a>
                )}
              </div>
            ))}
          </div>
          <button className="personas-suave" onClick={() => setClaves(null)}>
            Ya las anoté
          </button>
        </div>
      )}

      {listaActiva.length === 0 && !error && (camiones === null || conductores === null) && (
        <p className="cargando">Cargando…</p>
      )}

      {listaActiva.length === 0 && (camiones !== null || conductores !== null) && (
        <p className="cargando">No queda nada pendiente con esos filtros.</p>
      )}

      {listaActiva.length > 0 && (
        <div className="tabla-caja">
          <div className="tabla-scroll">
            <table>
              <thead>
                <tr>
                  {puedeCambiar() && (
                    <th style={{ width: 36 }}>
                      <input
                        type="checkbox"
                        aria-label="Seleccionar los de esta página"
                        checked={todosMarcados}
                        onChange={() => {
                          setMarcados((antes) => {
                            const s = new Set(antes)
                            if (todosMarcados) enPantallaIds.forEach((i) => s.delete(i))
                            else enPantallaIds.forEach((i) => s.add(i))
                            return s
                          })
                        }}
                      />
                    </th>
                  )}
                  <th>{esCamiones ? 'Placa' : 'Conductor'}</th>
                  <th>Cuánto mueve</th>
                  <th>Estado del viaje</th>
                  <th>{esCamiones ? 'Visto por primera vez' : 'Último viaje'}</th>
                  <th>{esCamiones ? 'De quién es' : 'Acceso a la app'}</th>
                </tr>
              </thead>
              <tbody>
                {pag.enPantalla.map((f) => {
                  const viajes = n(f.viajes)
                  const rodando = n(f.en_calle)
                  const cam = esCamiones ? (f as Camion) : null
                  const con = esCamiones ? null : (f as Conductor)
                  return (
                    <tr key={f.id}>
                      {puedeCambiar() && (
                        <td>
                          <input
                            type="checkbox"
                            aria-label={cam?.placa ?? con?.cedula ?? ''}
                            checked={marcados.has(f.id)}
                            onChange={() => alternar(f.id)}
                          />
                        </td>
                      )}
                      <td>
                        <span className="num" style={{ fontWeight: 600 }}>
                          {cam ? cam.placa : conPuntos(con?.cedula ?? '')}
                        </span>
                        {/* ⭐ 24-ago-2026: el nombre SÍ llega —medido, 604 de 604—.
                            Decía «Solo tenemos la cédula», que ya es falso. */}
                        {cam?.tipo_vehiculo && <small>{cam.tipo_vehiculo}</small>}
                        {con?.nombre && <small>{con.nombre}</small>}
                      </td>
                      <td>
                        <span className="num" style={{ fontWeight: 600 }}>
                          {viajes} {viajes === 1 ? 'viaje' : 'viajes'}
                        </span>
                        <small>
                          {n(f.viajes_mes) > 0 ? `${n(f.viajes_mes)} este mes` : 'ninguno este mes'}
                        </small>
                      </td>
                      <td>
                        {rodando > 0 ? (
                          <span className="et et-mal">
                            {esCamiones ? `${rodando} en la calle ahora` : 'Rodando ahora'}
                          </span>
                        ) : (
                          <span className="et et-neu">Sin viaje activo</span>
                        )}
                      </td>
                      <td>
                        {cam ? (
                          <>
                            {haceCuanto(cam.creado_at)}
                            <small className="num">{fechaCorta(cam.creado_at)}</small>
                          </>
                        ) : (
                          <>
                            {haceCuanto(con?.ultimo_viaje ?? null)}
                            {con?.placas && <small className="num">{con.placas.split(',')[0]}</small>}
                          </>
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {!puedeCambiar() ? (
                          <span className="et et-neu">Solo lectura</span>
                        ) : con?.estado === 'BLOQUEADO' ? (
                          // ⛔ Nada de tocarle el estado: se dice y se deja.
                          <span className="et et-mal">Bloqueado</span>
                        ) : esCamiones ? (
                          <>
                            <button
                              className="btn-tabla btn-tabla-fuerte"
                              disabled={guardando}
                              onClick={() => void clasificar([f.id], 'PROPIO')}
                            >
                              Propio
                            </button>
                            <button
                              className="btn-tabla"
                              style={{ marginLeft: 6 }}
                              disabled={guardando}
                              onClick={() => void clasificar([f.id], 'TERCERO')}
                            >
                              Tercero
                            </button>
                            {viajes >= 50 && (
                              <div className="sug">{viajes} viajes con nosotros: casi seguro propio</div>
                            )}
                            {viajes <= 1 && (
                              <div className="sug sug-gris">
                                {viajes === 0 ? 'Sin viajes' : 'Un solo viaje'}: probablemente de un tercero
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <button
                              className="btn-tabla btn-tabla-fuerte"
                              disabled={guardando}
                              onClick={() => {
                                setPidiendoCelular(con)
                                setCelular(con?.celular ?? '')
                                setClaves(null)
                              }}
                            >
                              Darle acceso
                            </button>
                            {con?.celular && <div className="sug sug-gris">Ya tiene celular</div>}
                            {viajes >= 20 && (
                              <div className="sug">{viajes} viajes: empieza por él</div>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <PieDeTabla {...pag} queSon={queSon} />
        </div>
      )}
    </section>
  )
}
